{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://kalmtide.outkraft.studio/schema/kalmtide.schema.json",
  "title": "KalmTide Preset",
  "description": "The single cross-platform contract. The web builder, both DSP engines (TS reference + Kotlin), the Workers backend, and the LLM generator all import this exact file. Forward-compatible: unknown fields are ignored; a major version bump gates on the 'schema' field. NOTE: some invariants cannot be expressed in pure JSON Schema and are enforced by the shared validator instead — flagged with 'Validator:' in descriptions below (segment ordering/contiguity, segments bounded by durationSec, unique layer ids).",
  "type": "object",
  "required": ["schema", "meta", "layers", "master"],
  "properties": {
    "schema": {
      "description": "Schema identifier and major version. Engines version-gate on the major number.",
      "const": "kalmtide/1"
    },
    "meta": { "$ref": "#/$defs/meta" },
    "allowFallback": {
      "description": "Designer opt-in (default false). When true, a platform that cannot deliver true binaural (speaker, mono BT, bone-conduction) may auto-swap binaural layers to their isochronic/monaural equivalent at the same beatHz profile. See Plan.MD §5.4.",
      "type": "boolean",
      "default": false
    },
    "layers": {
      "description": "Sound layers, mixed then passed through master. At least one layer.",
      "type": "array",
      "minItems": 1,
      "items": { "$ref": "#/$defs/layer" }
    },
    "master": { "$ref": "#/$defs/master" }
  },
  "$defs": {
    "meta": {
      "type": "object",
      "required": ["id", "name", "goal", "durationSec"],
      "properties": {
        "id": {
          "description": "Stable preset identity (UUID). Used as the sync primary key.",
          "type": "string",
          "format": "uuid"
        },
        "name": { "type": "string", "minLength": 1, "maxLength": 120 },
        "goal": { "$ref": "#/$defs/goal" },
        "author": { "type": "string", "maxLength": 120 },
        "durationSec": {
          "description": "Total session length in seconds. Validator: every segment/envelope time must fall within [0, durationSec].",
          "type": "number",
          "exclusiveMinimum": 0,
          "maximum": 86400
        },
        "created": {
          "description": "ISO 8601 UTC timestamp.",
          "type": "string",
          "format": "date-time"
        }
      }
    },
    "goal": {
      "description": "Drives goal-to-band presets and, on the backend, the LLM's goal->frequency mapping.",
      "type": "string",
      "enum": ["sleep", "meditate", "focus", "obe"]
    },
    "curve": {
      "description": "Interpolation shape between a ramp pair's start and end value over the segment.",
      "type": "string",
      "enum": ["hold", "linear", "exp"]
    },
    "waveform": {
      "description": "Carrier oscillator shape. Sine is the entrainment default; others are permitted but add harmonics.",
      "type": "string",
      "enum": ["sine", "triangle", "square", "sawtooth"],
      "default": "sine"
    },
    "carrierPair": {
      "description": "[startHz, endHz] ramp for the carrier tone over the segment (NOT left/right). Audible carrier range.",
      "type": "array",
      "prefixItems": [
        { "type": "number", "minimum": 20, "maximum": 20000 },
        { "type": "number", "minimum": 20, "maximum": 20000 }
      ],
      "minItems": 2,
      "maxItems": 2,
      "items": false
    },
    "beatPair": {
      "description": "[startHz, endHz] ramp for the beat frequency over the segment. Stored explicitly so left/right (carrier +/- beat/2) is always derivable.",
      "type": "array",
      "prefixItems": [
        { "type": "number", "minimum": 0, "maximum": 100 },
        { "type": "number", "minimum": 0, "maximum": 100 }
      ],
      "minItems": 2,
      "maxItems": 2,
      "items": false
    },
    "segment": {
      "description": "A time span with carrier + beat ramps. Validator: t0 < t1, segments sorted by t0, and boundaries contiguous within a layer (no gaps/overlap).",
      "type": "object",
      "required": ["t0", "t1", "carrierHz", "beatHz", "curve"],
      "properties": {
        "t0": { "type": "number", "minimum": 0 },
        "t1": { "type": "number", "exclusiveMinimum": 0 },
        "carrierHz": { "$ref": "#/$defs/carrierPair" },
        "beatHz": { "$ref": "#/$defs/beatPair" },
        "curve": { "$ref": "#/$defs/curve" }
      }
    },
    "envelopePoint": {
      "description": "A gain breakpoint at time t (seconds), linearly interpolated in dB to the next point.",
      "type": "object",
      "required": ["t", "gainDb"],
      "properties": {
        "t": { "type": "number", "minimum": 0 },
        "gainDb": { "$ref": "#/$defs/gainDb" }
      }
    },
    "gainDb": {
      "description": "Gain in decibels. All gains are dB, all times seconds, all frequencies Hz — no unit ambiguity.",
      "type": "number",
      "minimum": -120,
      "maximum": 12
    },
    "pan": {
      "description": "Stereo pan, -1 (hard left) to +1 (hard right), 0 center.",
      "type": "number",
      "minimum": -1,
      "maximum": 1,
      "default": 0
    },
    "layer": {
      "description": "Discriminated on 'type'. Exactly one branch matches.",
      "type": "object",
      "required": ["id", "type", "gainDb"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "type": {
          "type": "string",
          "enum": ["binaural", "monaural", "isochronic", "noise", "ambience", "voice"]
        },
        "gainDb": { "$ref": "#/$defs/gainDb" }
      },
      "oneOf": [
        { "$ref": "#/$defs/layerBinaural" },
        { "$ref": "#/$defs/layerMonaural" },
        { "$ref": "#/$defs/layerIsochronic" },
        { "$ref": "#/$defs/layerNoise" },
        { "$ref": "#/$defs/layerAmbience" },
        { "$ref": "#/$defs/layerVoice" }
      ]
    },
    "layerBinaural": {
      "description": "Two carriers offset by the beat, one per ear. Requires stereo headphones.",
      "type": "object",
      "required": ["type", "segments"],
      "properties": {
        "type": { "const": "binaural" },
        "waveform": { "$ref": "#/$defs/waveform" },
        "pan": { "$ref": "#/$defs/pan" },
        "segments": {
          "type": "array",
          "minItems": 1,
          "items": { "$ref": "#/$defs/segment" }
        }
      }
    },
    "layerMonaural": {
      "description": "Same fields as binaural, but the two carriers are summed pre-output so the beat is present in a single (mono-safe) signal.",
      "type": "object",
      "required": ["type", "segments"],
      "properties": {
        "type": { "const": "monaural" },
        "waveform": { "$ref": "#/$defs/waveform" },
        "pan": { "$ref": "#/$defs/pan" },
        "segments": {
          "type": "array",
          "minItems": 1,
          "items": { "$ref": "#/$defs/segment" }
        }
      }
    },
    "layerIsochronic": {
      "description": "A single carrier amplitude-gated at the beat frequency. gateShape MUST be raisedCosine — square gating clicks and is not permitted by the schema.",
      "type": "object",
      "required": ["type", "segments", "dutyCycle", "gateShape"],
      "properties": {
        "type": { "const": "isochronic" },
        "waveform": { "$ref": "#/$defs/waveform" },
        "pan": { "$ref": "#/$defs/pan" },
        "dutyCycle": {
          "description": "Fraction of each beat period the gate is open.",
          "type": "number",
          "exclusiveMinimum": 0,
          "exclusiveMaximum": 1
        },
        "gateShape": {
          "description": "Only raised-cosine is allowed (click-free). Enforces the 'never square-gate' rule at the schema level.",
          "type": "string",
          "const": "raisedCosine"
        },
        "segments": {
          "type": "array",
          "minItems": 1,
          "items": { "$ref": "#/$defs/segment" }
        }
      }
    },
    "layerNoise": {
      "description": "Colored noise bed with an optional gain envelope. Rendered with a seeded RNG for determinism.",
      "type": "object",
      "required": ["type", "color"],
      "properties": {
        "type": { "const": "noise" },
        "color": {
          "type": "string",
          "enum": ["white", "pink", "brown"]
        },
        "pan": { "$ref": "#/$defs/pan" },
        "envelope": {
          "type": "array",
          "minItems": 1,
          "items": { "$ref": "#/$defs/envelopePoint" }
        }
      }
    },
    "layerAmbience": {
      "description": "A prerecorded ambient bed referenced by manifest id, so web and Android can ship different file formats of the same asset.",
      "type": "object",
      "required": ["type", "asset"],
      "properties": {
        "type": { "const": "ambience" },
        "asset": {
          "description": "Manifest asset id (e.g. 'rain_soft'), resolved per-platform.",
          "type": "string",
          "minLength": 1
        },
        "pan": { "$ref": "#/$defs/pan" },
        "loop": { "type": "boolean", "default": true }
      }
    },
    "layerVoice": {
      "description": "A user- or TTS-supplied voice track, optionally ducking other layers while speaking.",
      "type": "object",
      "required": ["type", "source"],
      "properties": {
        "type": { "const": "voice" },
        "source": {
          "description": "Voice audio URI, e.g. 'user://guided_relax.mp3'.",
          "type": "string",
          "minLength": 1
        },
        "startSec": {
          "type": "number",
          "minimum": 0,
          "default": 0
        },
        "repeatEverySec": {
          "description": "If set, the voice asset restarts every repeatEverySec seconds after startSec, until meta.durationSec. Instances mix additively; ducking follows every instance.",
          "type": "number",
          "minimum": 1
        },
        "pan": { "$ref": "#/$defs/pan" },
        "ducking": {
          "type": "object",
          "required": ["targetLayerTypes", "duckDb"],
          "properties": {
            "targetLayerTypes": {
              "description": "Layer types attenuated while the voice plays.",
              "type": "array",
              "minItems": 1,
              "items": {
                "type": "string",
                "enum": ["binaural", "monaural", "isochronic", "noise", "ambience"]
              }
            },
            "duckDb": {
              "description": "Negative gain applied to target layers while ducking.",
              "type": "number",
              "minimum": -60,
              "maximum": 0
            }
          }
        }
      }
    },
    "master": {
      "description": "Global mix bus. Limiter is always applied before output.",
      "type": "object",
      "required": ["gainDb", "fadeInSec", "fadeOutSec", "limiter"],
      "properties": {
        "gainDb": { "$ref": "#/$defs/gainDb" },
        "fadeInSec": { "type": "number", "minimum": 0 },
        "fadeOutSec": { "type": "number", "minimum": 0 },
        "limiter": { "type": "boolean" }
      }
    }
  }
}
